/**
 * Browser transports: Web Bluetooth (Chrome/Edge on a computer or Android)
 * and Web Serial (Chrome/Edge on a computer, e.g. the COM port Windows sets up
 * for a paired Bluetooth printer). Safari and Firefox have neither; there the
 * app prints a PDF, or the native app prints over BLE.
 */
import { writeChunked, type Transport } from "../print.ts";
import type { PrinterModel } from "../registry.ts";

/* --- Device API typings (not in TypeScript's DOM lib) ---------------------- */

interface BleCharacteristic {
  uuid: string;
  properties: { write: boolean; writeWithoutResponse: boolean };
  writeValueWithResponse(data: BufferSource): Promise<void>;
  writeValueWithoutResponse(data: BufferSource): Promise<void>;
}
interface BleService {
  uuid: string;
  getCharacteristics(): Promise<BleCharacteristic[]>;
}
interface BleServer {
  getPrimaryServices(): Promise<BleService[]>;
  disconnect(): void;
}
export interface WebBleDevice extends EventTarget {
  name?: string;
  gatt?: { connect(): Promise<BleServer> };
}
interface BluetoothApi {
  requestDevice(options: {
    filters?: Array<{ namePrefix: string }>;
    acceptAllDevices?: boolean;
    optionalServices: string[];
  }): Promise<WebBleDevice>;
  getDevices?(): Promise<WebBleDevice[]>;
}
export interface WebSerialPort {
  open(options: { baudRate: number }): Promise<void>;
  close(): Promise<void>;
  writable: WritableStream<Uint8Array> | null;
}
interface SerialApi {
  requestPort(): Promise<WebSerialPort>;
  getPorts(): Promise<WebSerialPort[]>;
}

const nav = (typeof navigator === "undefined" ? {} : navigator) as { bluetooth?: BluetoothApi; serial?: SerialApi };
export const canWebBluetooth = !!nav.bluetooth;
export const canWebSerial = !!nav.serial;

/* --- Web Bluetooth ----------------------------------------------------------- */

/** Pick the printer from the browser's Bluetooth list. `showAll` lists every
 *  nearby device, for when the printer advertises under another name. */
export async function requestWebBluetooth(
  model: PrinterModel,
  showAll = false,
  onDisconnect?: () => void
): Promise<Transport> {
  const ble = model.ble;
  if (!ble) throw new Error(`The ${model.name} has no Bluetooth LE connection.`);
  if (!nav.bluetooth) throw new Error("This browser can't use Bluetooth. Use Chrome or Edge, or the phone app.");
  const optionalServices = [...ble.services];
  const device = await nav.bluetooth.requestDevice(
    showAll
      ? { acceptAllDevices: true, optionalServices }
      : { filters: ble.namePrefixes.map((namePrefix) => ({ namePrefix })), optionalServices }
  );
  return openWebBluetooth(model, device, onDisconnect);
}

/** Reopen a Bluetooth printer this browser was allowed before, without a picker. */
export async function rememberedWebBluetooth(model: PrinterModel, onDisconnect?: () => void): Promise<Transport | null> {
  const ble = model.ble;
  if (!ble || !nav.bluetooth?.getDevices) return null;
  const devices = await nav.bluetooth.getDevices();
  const device = devices.find((d) => ble.namePrefixes.some((p) => d.name?.startsWith(p)));
  return device ? openWebBluetooth(model, device, onDisconnect) : null;
}

export async function openWebBluetooth(
  model: PrinterModel,
  device: WebBleDevice,
  onDisconnect?: () => void
): Promise<Transport> {
  const ble = model.ble;
  if (!ble) throw new Error(`The ${model.name} has no Bluetooth LE connection.`);
  if (!device.gatt) throw new Error("That device doesn't accept Bluetooth connections");
  const server = await device.gatt.connect();
  const rank = (uuid: string) => {
    const i = ble.services.indexOf(uuid.toLowerCase());
    return i < 0 ? ble.services.length : i;
  };
  const services = (await server.getPrimaryServices()).sort((a, b) => rank(a.uuid) - rank(b.uuid));
  let target: BleCharacteristic | null = null;
  for (const service of services) {
    const chars = await service.getCharacteristics().catch(() => [] as BleCharacteristic[]);
    target = chars.find((c) => c.properties.writeWithoutResponse) ?? chars.find((c) => c.properties.write) ?? null;
    if (target) break;
  }
  if (!target) {
    server.disconnect();
    throw new Error(`Connected to ${device.name ?? "the device"}, but it offers no way to send a label.`);
  }
  const channel = target;
  if (onDisconnect) device.addEventListener("gattserverdisconnected", onDisconnect);
  return {
    name: device.name || "Bluetooth printer",
    kind: "ble",
    write: (data) =>
      writeChunked(
        data,
        { chunkBytes: ble.maxChunkBytes, pauseEveryBytes: ble.pauseEveryBytes, pauseMs: ble.pauseMs },
        (part) =>
          channel.properties.writeWithoutResponse
            ? channel.writeValueWithoutResponse(part)
            : channel.writeValueWithResponse(part)
      ),
    close: async () => server.disconnect(),
  };
}

/* --- Web Serial ------------------------------------------------------------- */

/** Pick the printer's serial port (on Windows, the paired printer's COM port). */
export async function requestWebSerial(model: PrinterModel): Promise<Transport> {
  if (!nav.serial) throw new Error("This browser can't open serial ports. Use Chrome or Edge on a computer.");
  return openWebSerial(model, await nav.serial.requestPort());
}

/** Reopen a serial port this browser was allowed before, without a picker. */
export async function rememberedWebSerial(model: PrinterModel): Promise<Transport | null> {
  const [port] = (await nav.serial?.getPorts()) ?? [];
  return port ? openWebSerial(model, port) : null;
}

export async function openWebSerial(model: PrinterModel, port: WebSerialPort): Promise<Transport> {
  await port.open({ baudRate: model.serial?.baudRate ?? 9600 }).catch((err: unknown) => {
    if (!(err instanceof DOMException && err.name === "InvalidStateError")) throw err; // already open
  });
  return {
    name: "printer on COM port",
    kind: "serial",
    async write(data) {
      if (!port.writable) throw new Error("The serial port closed");
      const writer = port.writable.getWriter();
      try {
        await writer.write(data);
      } finally {
        writer.releaseLock();
      }
    },
    close: () => port.close(),
  };
}
